/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import {downloadFile, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import Account, {ImportingStat} from '../../../models/accountModel';
import BolProduct from '../../../models/bolProductModel';
import {hasAdminLogin} from '../../../util/adminUtil';
import {useIsFirstRender} from '../../../util/componentUtil';
import {onSetCrumbs} from '../../../util/events';
import {ActivateHandle, IdleState} from '../../../util/interfaces';
import {exportVATs, getDefaultVats, getProductImages} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {CountryVATValues} from '../../../util/vatUtil';
import {trackPlatformAction} from '../../../util/analytics';
import {AppContext} from '../../misc/appContext';
import {useIdleObserver} from '../../misc/idleObserver';
import DefaultVATSettingsController from './defaultVat/defaultVATSettingsController';
import ImportVatController from './import/importVatController';
import ProductVATController from './product/productVATController';
import VATSettings from './vatSettings';
import {useTranslation} from 'react-i18next';

const SEARCH_DELAY = 500; // ms
const NUM_CHECK_DELAY = 5000; // ms
type LoadParams = {page?: number; limit?: number; search?: string};

export default function VATSettingsController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [products, setProducts] = useState<Array<BolProduct>>([]);
    const [loading, setLoading] = useState(false);
    const [importingProducts, setImportingProducts] = useState(false);
    const [importingCount, setImportingCount] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [defaultVATs, setDefaultVATs] = useState<CountryVATValues>(new CountryVATValues());
    const [showHiddenProducts, setShowHiddenProducts] = useState(false);
    const firstRender = useIsFirstRender();
    const countTimeout = useRef<number | undefined>(undefined);
    const searchTimeout = useRef<number | undefined>(undefined);
    const uploadRef = useRef<ActivateHandle>(null);
    const productRef = useRef<ActivateHandle>(null);
    const defaultVATsRef = useRef<ActivateHandle>(null);
    const imagesAbortController = useRef<AbortController | null>(new AbortController());
    useIdleObserver(onIdleStateChange);

    function onIdleStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearCountCheck();
        else checkImportingCount();
    }

    function onToggleShowHidden() {
        setShowHiddenProducts(!showHiddenProducts);
    }

    function onChangeSearch(value: string) {
        clearSearchTimeout();
        setSearch(value);
        searchTimeout.current = window.setTimeout(() => {
            setPage(0);
            loadProducts({search: value, page: 0});
        }, SEARCH_DELAY);
    }

    function onEnterSearch() {
        clearSearchTimeout();
        loadProducts();
    }

    function onClickSync() {
        if (!account) return;
        setLoading(true);
        BolProduct.importAsync(account.id, false, (_: Account | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                checkImportingCount();
                showSnackbar(t('settings.vat.fetchingProducts', 'De producten worden opgehaald'));
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('settings.vat.fetchFailedMsg', 'Het ophalen van de producten is niet gelukt.')
                    ),
                    t('settings.vat.fetchFailedTitle', 'Ophalen mislukt')
                );
        });
    }

    function onClickImportExport() {
        showDialog(
            t('settings.vat.importExportConfirm', 'Wil je producten importeren of exporteren?'),
            t('settings.vat.importExportTitle', 'Importeren & Exporteren'),
            [
                {label: t('settings.vat.importBtn', 'Importeren'), onClick: onClickBulkUpload, primary: false},
                {label: t('settings.vat.exportBtn', 'Exporteren'), onClick: onClickExport, primary: false}
            ]
        );
    }

    function onClickExport() {
        if (!account) return;
        setLoading(true);
        exportVATs({
            account_id: account.id,
            callback: (data: ResponseData, response: Response) => {
                if (data.status === 'success')
                    response.blob().then((blob: Blob) => {
                        let fileName = 'producten-export.xlsx';
                        const header = response.headers.get('Content-Disposition');
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setLoading(false);
                    });
                else {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.vat.exportFailedMsg', 'Het exporteren van de producten is niet gelukt.')
                        ),
                        t('settings.vat.exportFailedTitle', 'Exporteren mislukt')
                    );
                }
            }
        });
    }

    function onClickBulkUpload() {
        uploadRef.current?.activate();
    }

    function onClickProduct(product: BolProduct) {
        return function () {
            productRef.current?.activate(product, defaultVATs);
        };
    }

    function onClickDefault() {
        defaultVATsRef.current?.activate(defaultVATs);
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadProducts({page: nextPage});
        };
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        loadProducts({limit: limit});
    }

    function onDefaultVATChanged(values: CountryVATValues) {
        trackPlatformAction('settings.vat', 'save_settings');
        setDefaultVATs(values);
    }

    function onProductVATChanged(product: BolProduct) {
        for (let ix = 0; ix < products.length; ix++) {
            if (products[ix].id === product.id) {
                products[ix].title = product.title;
                products[ix].vats = product.vats;
                setProducts(products.slice());
                return;
            }
        }
    }

    function onHideProduct(product: BolProduct) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            showDialog(
                <p>
                    {t(
                        'settings.vat.hideProductExplain',
                        "Als je het product verbergt, dan wordt deze ook niet meer meegenomen op andere pagina's, zoals de Review Inzicht en Sales Dashboard pagina. Als je een product toch weer wilt tonen, dan kun je het product via de zoekbalk opzoeken."
                    )}
                    <br />
                    <br />
                    {t('settings.vat.hideProductConfirm', 'Weet je zeker dat je het product wilt verbergen?')}
                </p>,
                t('settings.vat.hideProductTitle', 'Product verbergen'),
                [
                    t('settings.vat.no', 'Nee'),
                    {
                        label: t('settings.vat.yesHideProduct', 'Ja, product verbergen'),
                        onClick: onConfirmHideProduct(product),
                        primary: false
                    }
                ]
            );
        };
    }

    function onConfirmHideProduct(product: BolProduct) {
        return function () {
            setLoading(true);
            product.hiddenByUser = true;
            product.update(false, (_: BolProduct, success: boolean, data: ResponseData) => {
                if (success) {
                    showSnackbar(t('settings.vat.productHidden', 'Het product is verborgen'));
                    loadProducts();
                } else {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.vat.hideFailedMsg', 'Het verbergen van het product is niet gelukt.')
                        ),
                        t('settings.vat.hideFailedTitle', 'Verbergen mislukt')
                    );
                }
            });
        };
    }

    function onUnHideProduct(product: BolProduct) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            setLoading(true);
            product.hiddenByUser = false;
            product.update(false, (_: BolProduct, success: boolean, data: ResponseData) => {
                if (success) {
                    showSnackbar(t('settings.vat.productUnhidden', 'Het product wordt weer getoond'));
                    loadProducts();
                } else {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.vat.unhideFailedMsg', 'Het opnieuw tonen van het product is niet gelukt.')
                        ),
                        t('settings.vat.unhideFailedTitle', 'Opnieuw tonen mislukt')
                    );
                }
            });
        };
    }

    function checkImportingCount() {
        if (!account) return;
        account.getImportingProductsCount().then((data: ImportingStat) => {
            if (data.importing) countTimeout.current = window.setTimeout(checkImportingCount, NUM_CHECK_DELAY);
            setImportingCount(data.products);
            setImportingProducts(data.importing);
        });
    }

    function loadProducts(params?: LoadParams) {
        if (!account) return;
        imagesAbortController.current?.abort();
        const _page = params?.page ?? page;
        const _limit = params?.limit ?? limit;
        const _search = params?.search ?? search;
        setLoading(true);
        BolProduct.getAll(
            {
                accountId: account.id,
                page: _page,
                limit: _limit,
                search: _search,
                sortCustomVAT: true,
                withImages: false,
                hiddenProducts: showHiddenProducts
            },
            (products: Array<BolProduct>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setProducts(products);
                    loadImages(products);
                    if (_page === 0) setTotal(data.total);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.vat.loadFailedMsg', 'Het laden van de producten is niet gelukt.')
                        ),
                        t('settings.vat.loadFailedTitle', 'Laden mislukt')
                    );
            }
        );
    }

    function loadImages(products: Array<BolProduct>) {
        if (!account) return;
        const eans = products.filter((prod: BolProduct) => !prod.imageURL).map((product: BolProduct) => product.ean);
        if (!eans.length) return;
        imagesAbortController.current = getProductImages({
            data: {account_id: account.id, eans},
            callback: (data: ResponseData) => {
                if (!data.success) return;
                const images: Record<string, string> = data.images;
                const _products = products.slice();
                for (const [ean, imageURL] of Object.entries(images)) {
                    const product = _products.find((prod: BolProduct) => prod.ean === ean);
                    if (!product) continue;
                    product.imageURL = imageURL;
                }
                setProducts(_products);
            }
        });
    }

    function loadDefaultVats() {
        if (!account) return;
        getDefaultVats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) setDefaultVATs(new CountryVATValues(data.default_vats));
            }
        });
    }

    function saveAccountSetupProgress() {
        if (hasAdminLogin()) return;
        if (!account || account.setupProgress.vat) return;
        account.setupProgress.vat = true;
        account.update(undefined, undefined, undefined, (account: Account, success: boolean) => {
            if (success) context.onChangeContext({account});
        });
    }

    function clearCountCheck() {
        window.clearTimeout(countTimeout.current);
        countTimeout.current = undefined;
    }

    function clearSearchTimeout() {
        window.clearTimeout(searchTimeout.current);
        searchTimeout.current = undefined;
    }

    useEffect(() => {
        loadProducts();
        loadDefaultVats();
        checkImportingCount();
        saveAccountSetupProgress();

        return function () {
            clearCountCheck();
            clearSearchTimeout();
        };
    }, [account?.id]);

    useEffect(() => {
        if (firstRender) return;
        loadProducts();
    }, [showHiddenProducts]);

    useEffect(() => {
        if (firstRender) return;
        if (!importingProducts) loadProducts();
    }, [importingProducts]);

    useEffect(() => {
        onSetCrumbs([
            {label: t('settings.vat.crumbSettings', 'Instellingen')},
            {label: t('settings.vat.crumbProducts', 'Producten & BTW')}
        ]);

        return function () {
            clearCountCheck();
            clearSearchTimeout();
        };
    }, []);

    return (
        <>
            <VATSettings
                onClickSync={onClickSync}
                onClickImportExport={onClickImportExport}
                onClickProduct={onClickProduct}
                onClickDefault={onClickDefault}
                onChangeSearch={onChangeSearch}
                onEnterSearch={onEnterSearch}
                onChangePage={onChangePage}
                onChangeLimit={onChangeLimit}
                onHideProduct={onHideProduct}
                onUnHideProduct={onUnHideProduct}
                onToggleShowHidden={onToggleShowHidden}
                products={products}
                total={total}
                search={search}
                defaultVATs={defaultVATs}
                page={page}
                limit={limit}
                isImporting={importingProducts}
                importingCount={importingCount}
                loading={loading}
                showHiddenProducts={showHiddenProducts}
            />
            <DefaultVATSettingsController onSuccess={onDefaultVATChanged} ref={defaultVATsRef} />
            <ProductVATController onSuccess={onProductVATChanged} ref={productRef} />
            <ImportVatController ref={uploadRef} onSuccess={loadProducts} />
        </>
    );
}
